import React, { useEffect, useMemo, useState } from 'react';
import BannerSkillLevel from '@components/progress/BannerSkillLevel';
import {
  LoadingFull,
  LoadingScroll,
  Sheet,
  Snackbar,
  Scrim,
  PopUpFailedGenerateWorksheet,
  Button,
  PopUp,
} from 'geniebook-ui/dist';
import TableSkillLevel from '@components/progress/table/TableSkillLevel';
import { ITableItemSkillLevel } from '@components/progress/table/TableItem';
import ChartSkillLevel from '@components/progress/chart/ChartSkillLevel';
import { topicSkillLevel } from '@components/progress/sheet/SelectedTopic';
import {
  useMasteryStudentTopics,
  usePresetWorksheets,
  useStudentStatistics,
} from '@hooks/student.hook';
import { useNavigate, useParams } from 'react-router-dom';
import { subjectIdFromName } from '@utils/helpers/SubjectParser';
import WorksheetServices from '@services/GeniebookAPI/WorksheetServices.service';
import moment from 'moment';
import { PRESET_TYPE } from '@utils/constants/presetws.constants';
import Joyride, { CallBackProps, Props } from 'react-joyride';
import '../tour/Tour.css';
import { atomWithStorage } from 'jotai/utils';
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import MasterySurvey from '@layouts/MasterySurvey';
import { LS_MASTERY_POPUP_KEY } from '@utils/constants/AbGroup.constants';
import { masteryTopicRandomizer } from '@components/progress/MasteryTopicRandomizer';
import {
  JUNIOR_COLLEGE_STUDENT,
  USER_ACCOUNT_TYPE_DEMO,
} from '@utils/constants/user.constants';
import { useOnboardingTooltipPermission } from '@utils/helpers/useOnboardingTooltipPermission';
import { permissionShowOnboardingTooltip } from '@atom/worksheetAtom';
import { useViewWidth } from '@utils/helpers/viewWidth';
import {
  IMasteryTopic,
  IStatisticTopic,
} from '@models/interfaces/student.interface';
import { AxiosResponse } from 'axios';
import AbusingWorksheetWarning from '@layouts/AbusingWorksheetWarningPopup';
import { WARNING_MAX_TOPICAL_WORKSHEETS_REACHED } from '@utils/constants/errors.constants';
import TimedWorksheetOption from '@components/timed-worksheet/create-worksheet/TimedWorksheetOption';
import {
  INSUFFICIENT_WORKSHEET_ERROR_CODE,
  NEW_RECSYS_ERROR_LTA,
} from '@utils/constants/worksheet.constants';
import SkillLevelPopUpInfo from '@components/progress/table/SkillLevelPopUpInfo';
import {
  forceHideTimedWorksheetPopup,
  showCalculatingSkillLevelInfoPopup,
  showSkillLevelInfoPopup,
} from '@atom/progress';
import BreakdownSection from '@components/progress/breakdown/BreakdownSection';
import { masteryTopicsAtom } from '@components/progress/ProgressStore';
import TimedWorksheetOptionHorizontal from '@components/timed-worksheet/create-worksheet/TimedWorksheetOptionHorizontal';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import BreakdownOnboarding from '@components/progress/breakdown/onboarding/BreakdownOnboarding';
import GlobalStore from '@store/globalStore';
import CalculateSkillLevelInfo from '@components/progress/table/CalculateSkillLevelInfo';
import ProgressUpsellingBanner from '@components/subscription/list/ProgressUpsellingBanner';
import {
  haveSubjectForTrialAtom,
  userDetailQueryAtom,
} from '@atom/globalQueryAtom';
import { useIsJCUser } from '@hooks/user.hook';
import { CDN_URL } from '@utils/constants/url.constants';

const maxSelected = 3;
const breakpoint = require('geniebook-ui/dist/themes/breakpoint');
const alreadyTakeProgressTour = atomWithStorage(
  'alreadyTakeProgressTour',
  false,
);

export interface ProgressDashboardV2Props {
  toggleTimedOption: (value: boolean) => void;
}

const ProgressDashboardV2 = ({
  toggleTimedOption,
}: ProgressDashboardV2Props) => {
  // params url
  const { subjectId } = useParams();
  const { isJC } = useIsJCUser({
    subjectId: Number(subjectIdFromName(subjectId)),
  });
  // state hooks
  const viewWidth = useViewWidth();
  const [sortBy, setSortBy] = useState<string | number>('alphabet');
  const [topics, setTopics] = useState<ITableItemSkillLevel[]>([]);
  const [selectedTopics, setSelectedTopic] = useState<topicSkillLevel[]>([]);
  const [availableColor, setAvailableColor] = useState([
    {
      color: '#82DDF4',
      available: true,
    },
    {
      color: '#FED00B',
      available: true,
    },
    {
      color: '#A0E274',
      available: true,
    },
  ]);
  const [insufficientQuestionsPopup, setInsufficientQuestionsPopup] = useState<{
    show: boolean;
    subtopics: {
      id: number;
      name: string;
    }[];
  }>({
    show: false,
    subtopics: [],
  });
  const [averageScore, setAverageScore] = useState(0);
  const haveMasteryLevel =
    useFeatureIsOn('student_new_progress_page') || Boolean(isJC);
  const setMasteryTopics = useSetAtom(masteryTopicsAtom);
  const [loadingCreateWs, setLoadingCreateWs] = useState(false);
  const [error, setError] = useState('');
  const [showAbusingWorksheetWarning, setShowAbusingWorksheetWarning] =
    useState<boolean>(false);
  const [abuseWarningCode, setAbuseWarningCode] = useState<number>(0);
  const [illegalTopicIds, setIllegalTopicIds] = useState<string>('');
  const [illegalTopicNames, setIllegalTopicNames] = useState<string>('');
  const [errorFromLTA, setErrorFromLTA] = useState<boolean>(false);
  const [accountOverlayOpened, setAccountOverlayOpened] = useState(false);
  const [bottomSheetOffset, setBottomSheetOffset] =
    useState<React.CSSProperties>({ bottom: '0px' });
  const showSkillLevelInfoPopupState = useAtomValue(showSkillLevelInfoPopup, {
    store: GlobalStore,
  });
  const showCalculatingSkillLevelInfoState = useAtomValue(
    showCalculatingSkillLevelInfoPopup,
    {
      store: GlobalStore,
    },
  );
  const store = useStore({ store: GlobalStore });
  const navigate = useNavigate();
  const userDetail = useAtomValue(userDetailQueryAtom);
  const width = useViewWidth();
  const presetWorksheets = usePresetWorksheets({
    subject_id: subjectIdFromName(subjectId),
    student_id: parseInt(userDetail.data?.data?.user_id ?? ''),
    type: PRESET_TYPE.unassigned,
  });
  const hideTimedWorksheetPopup = useAtomValue(forceHideTimedWorksheetPopup, {
    store: GlobalStore,
  });
  const haveSubjectForTrial = useAtomValue(haveSubjectForTrialAtom);

  const [showMasterySurveyPopup, setShowMasterySurveyPopup] =
    useState<number>(-1);

  const [isTimedWorksheetEnabled, setIsTimedWorksheetEnabled] = useState(false);

  useOnboardingTooltipPermission();
  const [isAllowShowOnboardingTooltip] = useAtom(
    permissionShowOnboardingTooltip,
  );

  useEffect(() => {
    if (selectedTopics.length > 0) {
      toggleTimedOption(true);
    } else {
      toggleTimedOption(false);
    }
  }, [selectedTopics]);

  const showCreateWorksheetButton = !isJC && selectedTopics.length > 0;
  // tour lib
  const tourState: Props = {
    steps: [
      {
        target: '#tableSkillLevel',
        content: (
          <div>
            <p className={'tour-title text-primary-navyblue'}>
              Track your progress
            </p>
            <p className={'tour-desc text-neutral-pencil'}>
              Choose up to 3 topics to track your progress
            </p>
          </div>
        ),
        disableBeacon: true,
      },
    ],
    locale: { last: 'CLOSE', next: 'NEXT' },
    hideCloseButton: true,
    showProgress: false,
    showSkipButton: false,
    spotlightPadding: 10,
    disableOverlayClose: true,
    styles: {
      options: {
        zIndex: 10000,
      },
    },
    callback: handleCloseTour,
  };
  const [alreadyHaveTour, setAlreadyHaveTour] = useAtom(
    alreadyTakeProgressTour,
  );
  function handleCloseTour(data: CallBackProps) {
    if (data.action === 'close') {
      setAlreadyHaveTour(true);
    }
  }

  // querying topic from API
  const queryStudentTopics = useStudentStatistics({
    subject_id: subjectIdFromName(subjectId),
    student_id: userDetail?.data?.data.user_id
      ? parseInt(userDetail?.data?.data.user_id)
      : 0,
  });
  const queryMasteryStudentTopics = useMasteryStudentTopics(
    {
      subject_id: subjectIdFromName(subjectId),
    },
    haveMasteryLevel,
  );

  const checkSubscriptionDelayed = () => {
    setIsSubscriptionDelayed(fnIsSubscriptionDelayed());
  };

  const fnIsSubscriptionDelayed = () => {
    const delayedSubscriptionDate = JSON.parse(
      localStorage.getItem('delayed_subs_date') ?? '{}',
    )[subjectId ?? ''];

    return moment(delayedSubscriptionDate) > moment();
  };

  const [isSubscriptionDelayed, setIsSubscriptionDelayed] = useState(
    fnIsSubscriptionDelayed(),
  );

  useEffect(() => {
    checkSubscriptionDelayed();

    if (queryStudentTopics?.data?.data?.error === false && !haveMasteryLevel) {
      const data = queryStudentTopics.data.data.data;
      setTopics(
        sortingTopics(
          sortBy,
          data.map(item => parseTopicData(item)),
        ),
      );
    }
    if (
      queryMasteryStudentTopics?.data?.data?.error === false &&
      haveMasteryLevel
    ) {
      const data = queryMasteryStudentTopics.data.data.data;
      setMasteryTopics(data);
      let topicList = data.map(item => parseTopicData(item));
      if (userDetail.data?.data?.account_type == USER_ACCOUNT_TYPE_DEMO) {
        topicList = masteryTopicRandomizer(topicList);
      }
      setTopics(sortingTopics(sortBy, topicList));
    }
  }, [
    queryStudentTopics.data?.data,
    queryMasteryStudentTopics.data?.data,
    haveMasteryLevel,
  ]);

  function parseTopicData(
    data: IStatisticTopic | IMasteryTopic,
  ): ITableItemSkillLevel {
    let score: number;
    if (haveMasteryLevel) {
      const dataToParse = data as IMasteryTopic;
      score = Number(dataToParse.student_score);
    } else {
      const dataToParse = data as IStatisticTopic;
      score = Number(dataToParse.score);
    }
    if (isNaN(score)) {
      score = 0;
    }
    return {
      id: data.topic_id,
      topic: data.topic_name,
      level: data.level,
      skillLevel: score,
      checked: false,
      progress: data.progress || 0,
    };
  }

  // breakpoint
  const tabletBreakpoint = breakpoint.lg;

  function sortingTopics(
    sortType: string | number,
    data: ITableItemSkillLevel[],
  ) {
    return data.sort(function (a, b) {
      let sortNum = 0;
      switch (sortType) {
        case 'alphabet':
          if (b.level < a.level) {
            sortNum = -1;
          } else if (b.level == a.level) {
            sortNum = b.topic < a.topic ? 1 : -1;
          } else {
            sortNum = 1;
          }
          break;
        case 'strongest':
          if (a.skillLevel === 0) sortNum = 1;
          else if (b.skillLevel === 0) sortNum = -1;
          else sortNum = b.skillLevel - a.skillLevel;
          break;
        case 'weakest':
          if (a.skillLevel === 0) sortNum = 1;
          else if (b.skillLevel === 0) sortNum = -1;
          else sortNum = a.skillLevel - b.skillLevel;
          break;
      }
      return sortNum;
    });
  }
  function handleChangeSortBy(value: string | number) {
    setSortBy(value);
    setTopics(prevState => sortingTopics(value, prevState));
  }

  function handleCheckboxClick(id: number | string, newStatus: boolean) {
    let newTopicId: typeof id | null = null;
    let toRemoveId: typeof id | null = null;
    let colorAvailable: string = '';

    if (!newStatus) {
      toRemoveId = id;
      const toRemove = selectedTopics.filter(item => item.id === toRemoveId)[0];
      changeColorAvailable(toRemove.color, true);
    } else {
      if (selectedTopics.length < maxSelected) {
        newTopicId = id;
      } else {
        const toRemove = selectedTopics[0];
        toRemoveId = toRemove.id;
        changeColorAvailable(toRemove.color, true);
        colorAvailable = toRemove.color;
        newTopicId = id;
      }
    }

    addAndRemoveSelected(newTopicId, toRemoveId, colorAvailable);
  }

  function addAndRemoveSelected(
    newId: number | string | null,
    removeId: number | string | null,
    color: string,
  ) {
    const newTopicList: ITableItemSkillLevel[] = [];
    for (let i = 0; i < topics.length; i++) {
      const topic = topics[i];
      if (topics[i].id === removeId) {
        setSelectedTopic(prevState =>
          prevState.filter(item => item.id !== removeId),
        );
        topic.checked = false;
      }
      if (topics[i].id === newId && newId) {
        const colorSelection = availableColor.filter(item => item.available)[0];
        // because state is not updated synchronously, sometimes color selection will be empty, give back up color to accomodate it
        const colorToBeUse = colorSelection ? colorSelection.color : color;
        topic.color = colorToBeUse;
        topic.checked = true;
        changeColorAvailable(colorToBeUse, false);
        setSelectedTopic(prevState => {
          const newSelected: topicSkillLevel[] = [];
          newSelected.push(...prevState, {
            id: newId,
            label: topics[i].topic,
            color: colorToBeUse,
          });
          return newSelected;
        });
      }
      newTopicList.push(topic);
    }
    setTopics(newTopicList);
  }

  function changeColorAvailable(color: string, state: boolean) {
    setAvailableColor(prevState =>
      prevState.map(item => {
        if (item.color === color) {
          return {
            color: color,
            available: state,
          };
        } else {
          return {
            color: item.color,
            available: item.available,
          };
        }
      }),
    );
  }

  function getAverageScore(systemLevel: number | null) {
    let count = 0;
    let sum = 0;
    const JCLevel = Object.values(JUNIOR_COLLEGE_STUDENT) as string[];
    const levelIsJC = (level: string) => {
      return JCLevel.includes(level);
    };
    const systemLevelJC = levelIsJC(String(systemLevel));

    topics.forEach(item => {
      if (haveMasteryLevel) {
        if (systemLevel) {
          const levels = item.level.split(',');
          let logicGate = !!levels.find(value =>
            systemLevelJC ? levelIsJC(value) : parseInt(value) == systemLevel,
          );

          if (systemLevel != 1 && systemLevel != 7 && !systemLevelJC) {
            // Subject-skill level to average topics across current academic level AND 1 level below current level (except S1 & P1)
            logicGate =
              logicGate ||
              !!levels.find(value => parseInt(value) == systemLevel - 1);
          }

          if (logicGate && item.skillLevel > 0) {
            count++;
            sum += item.skillLevel;
          }
        }
      } else {
        count++;
        sum += item.skillLevel;
      }
    });
    return sum === 0 ? 0 : sum / count;
  }
  useEffect(() => {
    if (userDetail.data?.data?.system_level != undefined) {
      const systemLevel = parseInt(userDetail.data?.data?.system_level);
      setAverageScore(getAverageScore(systemLevel));
    }
  }, [topics, userDetail.data, haveMasteryLevel]);

  async function check_abusing_worksheet() {
    const topicIds: number[] = [];

    selectedTopics.forEach((item: topicSkillLevel, index: number) => {
      topicIds[index] = parseInt(item.id + '');
    });

    const res: AxiosResponse = await WorksheetServices.getAbusedWorksheetData({
      topic_ids: topicIds.toString(),
      subject_id: subjectIdFromName(subjectId),
    });

    const { data } = res;
    const response = data?.data;

    if (!response) return;

    if (response.warning_code === 0) {
      setAbuseWarningCode(0);
      setIllegalTopicIds('');
      await createWorksheet();
    } else {
      setAbuseWarningCode(response.warning_code);
      setIllegalTopicIds(response.disallowed_topic_ids.toString());

      if (response.warning_code === WARNING_MAX_TOPICAL_WORKSHEETS_REACHED) {
        const dissalowed_topic_names =
          response.disallowed_topic_names.join(', ');

        setIllegalTopicNames(dissalowed_topic_names);
      }

      setShowAbusingWorksheetWarning(true);
    }
  }

  async function createWorksheet() {
    try {
      setLoadingCreateWs(true);
      const ids = selectedTopics.map(item => item.id);
      const presets = presetWorksheets.data?.data?.data;
      if (ids.length == 1 && !haveAttemptStarterWs() && presets) {
        const selectedPreset = presets.filter(
          item => item.topic_id == selectedTopics[0].id,
        )[0];
        const createWs = await WorksheetServices.assignStarterWorksheet({
          student_id: userDetail.data?.data?.user_id
            ? parseInt(userDetail.data.data.user_id)
            : 0,
          worksheet_id: selectedPreset.worksheet_id,
          due_date: moment().add(2, 'weeks').format('YYYY-MM-DD'),
          is_timed_worksheet: isTimedWorksheetEnabled,
        });
        if (createWs.data.error) {
          setError(createWs.data.error_msg);
          setTimeout(() => setError(''), 5000);
          return;
        }
        navigate(
          `/${subjectId}/worksheets/worksheet_question/${selectedPreset.worksheet_id}`,
        );
      } else {
        const createWs = await WorksheetServices.createRevisionWorksheet({
          subject_id: subjectIdFromName(subjectId),
          topic_ids: ids.join(','),
          disallowed_topic_ids: illegalTopicIds,
          abuse_warning_code: abuseWarningCode,
          is_timed_worksheet: isTimedWorksheetEnabled,
        });
        if (createWs.data.error) {
          if (createWs.data.error_code === INSUFFICIENT_WORKSHEET_ERROR_CODE) {
            // Insufficient questions from new recsys
            // show popup
            setInsufficientQuestionsPopup({
              show: true,
              subtopics: selectedTopics.map(data => ({
                id: parseInt(data.id + ''),
                name: data.label,
              })),
            });
            return;
          } else if (createWs.data.error_code === NEW_RECSYS_ERROR_LTA) {
            setError(createWs.data.error_msg);
            setErrorFromLTA(true);
            return;
          } else {
            setError(createWs.data.error_msg);
            setTimeout(() => setError(''), 5000);
            return;
          }
        }
        navigate(
          `/${subjectId}/worksheets/worksheet_question/${createWs.data.data.worksheet_id}`,
        );
      }
    } catch (e) {
      setError("Can't create worksheet");
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoadingCreateWs(false);
    }
  }

  function haveAttemptStarterWs() {
    const presets = presetWorksheets.data?.data?.data;
    if (presets) {
      for (let i = 0; i < selectedTopics.length; i++) {
        const filter = presets.filter(
          value => value.topic_id == selectedTopics[i].id,
        );
        if (filter.length > 0) {
          return false;
        }
      }
      return true;
    }
    return true;
  }

  const isSubjectInMastery = (
    subject_ids: string[],
    subject_id: string,
  ): boolean => {
    if (subject_id === '') return false;

    return subject_ids.includes(subject_id);
  };

  useEffect(() => {
    const subject_is_mastery =
      userDetail?.data?.data.show_mastery_survey.show_mastery_popup &&
      isSubjectInMastery(
        userDetail?.data?.data.show_mastery_survey.subject_ids,
        subjectIdFromName(subjectId ?? '').toString(),
      );

    if (
      alreadyHaveTour &&
      userDetail.isSuccess &&
      subject_is_mastery &&
      showMasterySurveyPopup === -1
    ) {
      show_mastery_popup();
    }
  }, [userDetail, alreadyHaveTour]);

  const show_mastery_popup = () => {
    const last_popup_date = localStorage.getItem(LS_MASTERY_POPUP_KEY);

    if (last_popup_date !== null) {
      const today = moment();

      if (
        moment().day() === 1 &&
        moment().month() === 7 &&
        moment(last_popup_date).format(moment.HTML5_FMT.DATE) ===
          today.clone().subtract(1, 'week').format(moment.HTML5_FMT.DATE)
      ) {
        setShowMasterySurveyPopup(1);
      }
    } else setShowMasterySurveyPopup(1);
  };

  const showMasterySurvey = () => {
    const currentDate = moment();

    if (currentDate.month() !== 7) return false;

    setShowMasterySurveyPopup(0);

    let gb_popup_date: string;

    if (currentDate.day() === 1) {
      gb_popup_date = currentDate.format('YYYYMMDD');
    } else if (currentDate.day() > 1) {
      const previousMonday = currentDate.day(1);

      gb_popup_date = previousMonday.format('YYYYMMDD');
    } else {
      const previousWeekMonday = currentDate.day(-7).day(1);

      gb_popup_date = previousWeekMonday.format('YYYYMMDD');
    }

    localStorage.setItem(LS_MASTERY_POPUP_KEY, gb_popup_date);
  };

  const worksheetAbuseAction = (proceedToGenerate: boolean) => {
    setShowAbusingWorksheetWarning(false);

    if (proceedToGenerate) {
      createWorksheet();
    } else {
      setAbuseWarningCode(0);
      setIllegalTopicIds('');
    }
  };

  const showInfoPopup = (show: boolean) => {
    store.set(forceHideTimedWorksheetPopup, show);

    store.set(showSkillLevelInfoPopup, show);
  };

  const showCalculatingSkillLevelPopup = (show: boolean) => {
    store.set(forceHideTimedWorksheetPopup, show);

    store.set(showCalculatingSkillLevelInfoPopup, show);
  };

  const [
    showDelayedSubscriptionMessageSnackbar,
    setShowDelayedSubscriptionMessageSnackbar,
  ] = useState<boolean>(false);

  const [delayedStartDateMessage, setDelayedStartDateMessage] =
    useState<string>('');

  const handleShowSubscriptionDelayedError = () => {
    const effectiveStartDate = JSON.parse(
      localStorage.getItem('delayed_subs_date') ?? '{}',
    )[subjectId ?? ''];

    setDelayedStartDateMessage(
      'Excited to do the worksheets? Your subscription will start on ' +
        moment(effectiveStartDate).format('DD MMM YYYY') +
        '. See you then!',
    );
    setShowDelayedSubscriptionMessageSnackbar(true);
  };

  const loadingScreen = (
    <Scrim onClose={() => {}}>
      <LoadingScroll
        {...(isJC
          ? {
              jcLoading: (
                <img
                  src={`${CDN_URL}assets/animations/GB-loading-minimalistic.gif`}
                  className="mb-5"
                  alt="loading spinner"
                  width={25}
                  height={25}
                />
              ),
            }
          : {})}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
      />
    </Scrim>
  );

  // Memoize the navbar height based on window resize
  const navbarHeight = useMemo(() => {
    const navbar = document.getElementById('bottom-navbar');
    return navbar ? getComputedStyle(navbar).height : '0px';
  }, [window.innerWidth]);

  useEffect(() => {
    setBottomSheetOffset({ bottom: navbarHeight });
  }, [navbarHeight]);

  // observe the DOM to detect if account overlay is opened or not
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.type === 'childList') {
        const accountMenuOverlay = document.querySelector(
          'div#account-menu-small-screen',
        );

        // this state will affect the re-render of the bottom sheet
        accountMenuOverlay
          ? setAccountOverlayOpened(true)
          : setAccountOverlayOpened(false);
      }
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  return (
    <div id="progress-dashboard">
      {showDelayedSubscriptionMessageSnackbar && (
        <Snackbar
          message={delayedStartDateMessage}
          views={viewWidth < breakpoint.md ? 'mobile' : 'desktop'}
          buttonText={'Dismiss'}
          onTextClick={() => {
            setShowDelayedSubscriptionMessageSnackbar(false);
          }}
          states={'error'}
          className={'fixed center-horizontal top-[80px]'}
        />
      )}
      {showCalculatingSkillLevelInfoState ? (
        width > breakpoint.md ? (
          <PopUp
            className={'!max-w-[463px] !py-6 !px-8'}
            onClose={() => showCalculatingSkillLevelPopup(false)}
            buttonGroup={
              <Button
                className={'!max-w-full'}
                onClick={() => showCalculatingSkillLevelPopup(false)}
              >
                Got it
              </Button>
            }
            description={<CalculateSkillLevelInfo />}
            title={
              <div className={'font-black text-primary-navyblue text-xl'}>
                How to Calculate Skill Levels
              </div>
            }
          />
        ) : (
          <Sheet
            isOpen={showCalculatingSkillLevelInfoState}
            toggleSheet={() => showCalculatingSkillLevelPopup(false)}
            zIndex={102}
            darkMode={false}
          >
            <div
              className={
                'p-4 !pb-6 w-full text-center max-h-[80vh] overflow-auto'
              }
            >
              <div className={'font-black mb-4 text-primary-navyblue text-xl'}>
                How to Calculate Skill Levels
              </div>
              <div className={'mb-6'}>
                <CalculateSkillLevelInfo />
              </div>
              <Button
                className={'!max-w-full hover:!scale-100'}
                onClick={() => showCalculatingSkillLevelPopup(false)}
              >
                Close
              </Button>
            </div>
          </Sheet>
        )
      ) : (
        <></>
      )}
      {showSkillLevelInfoPopupState ? (
        width > breakpoint.md ? (
          <PopUp
            className={'!max-w-[460px] !py-6 !px-8'}
            onClose={() => showInfoPopup(false)}
            buttonGroup={
              <Button
                className={'!max-w-full'}
                onClick={() => showInfoPopup(false)}
              >
                Got it
              </Button>
            }
            description={
              <SkillLevelPopUpInfo levelId={userDetail.data?.data.level_id!} />
            }
            title={
              <div className={'font-black text-primary-navyblue text-xl'}>
                How to Read Skill Levels
              </div>
            }
          />
        ) : (
          <Sheet
            isOpen={showSkillLevelInfoPopupState}
            toggleSheet={() => showInfoPopup(false)}
            zIndex={102}
            darkMode={false}
          >
            <div
              className={
                'p-4 !pb-6 w-full text-center max-h-[80vh] overflow-auto'
              }
            >
              <div className={'font-black mb-4 text-primary-navyblue text-xl'}>
                How to Read Skill Levels
              </div>
              <div className={'mb-6'}>
                <SkillLevelPopUpInfo
                  levelId={userDetail.data?.data.level_id!}
                />
              </div>
              <Button
                className={'!max-w-full hover:!scale-100'}
                onClick={() => showInfoPopup(false)}
              >
                Close
              </Button>
            </div>
          </Sheet>
        )
      ) : null}
      {showAbusingWorksheetWarning ? (
        <AbusingWorksheetWarning
          warningCode={abuseWarningCode}
          topicNames={illegalTopicNames}
          proceedToGenerate={worksheetAbuseAction}
        />
      ) : (
        ''
      )}
      {showMasterySurveyPopup === 1 ? (
        <MasterySurvey showMasterySurvey={showMasterySurvey} />
      ) : (
        ''
      )}
      {isAllowShowOnboardingTooltip && !alreadyHaveTour ? (
        <Joyride {...tourState} />
      ) : null}
      {(viewWidth >= tabletBreakpoint && haveMasteryLevel) || Boolean(isJC) ? (
        <BannerSkillLevel
          subject={subjectId ?? ''}
          skillLevel={averageScore}
          className={'mb-6'}
          onClickLearnMore={() => {
            showInfoPopup(true);
          }}
          onClickHowCalculateSkillLevel={() => {
            showCalculatingSkillLevelPopup(true);
          }}
        />
      ) : null}
      <div className={'flex w-full gap-4 max-lg:flex-col'}>
        <div className={'flex-auto relative'}>
          {topics ? (
            <TableSkillLevel
              list={topics}
              onCheckboxClick={handleCheckboxClick}
              haveMasteryLevel={haveMasteryLevel}
              onSortSkillLevel={sortBy => {
                handleChangeSortBy(sortBy);
              }}
              sortBySkillLevel={sortBy}
              hasSelectedItems={selectedTopics.length > 0}
            />
          ) : null}
          {showCreateWorksheetButton && viewWidth >= tabletBreakpoint && (
            <TimedWorksheetOptionHorizontal
              createWorksheet={check_abusing_worksheet}
              onSetTimedWorksheet={setIsTimedWorksheetEnabled}
              className={'absolute bottom-[24px] left-0'}
              isSubscriptionDelayed={isSubscriptionDelayed}
              handleShowSubscriptionDelayedError={
                handleShowSubscriptionDelayedError
              }
            />
          )}
        </div>
        <div
          className="flex flex-col overflow-scroll no-scrollbar"
          id="information-skill-level"
        >
          {viewWidth >= tabletBreakpoint ? (
            <>
              <div className="max-w-[340px] w-[340px] overflow-auto no-scrollbar">
                <BreakdownSection />
                <ChartSkillLevel
                  selectedTopics={selectedTopics}
                  haveMasteryLevel={haveMasteryLevel}
                  subjectId={subjectIdFromName(subjectId)}
                  preventLoadingAnimation={queryStudentTopics.isLoading}
                  isTestAccount={
                    userDetail.data?.data?.account_type ==
                    USER_ACCOUNT_TYPE_DEMO
                  }
                  topics={topics}
                />
              </div>
            </>
          ) : null}
          {haveSubjectForTrial && (
            <div className="lg:max-w-[340px] lg:w-[340px] text-white max-lg:w-full mb-6">
              <ProgressUpsellingBanner />
            </div>
          )}
          {selectedTopics.length > 0 && viewWidth < tabletBreakpoint && (
            <div className={'lg:hidden max-lg:h-[140px]'}></div>
          )}
        </div>
      </div>
      {viewWidth < tabletBreakpoint && !hideTimedWorksheetPopup ? (
        <div>
          {showCreateWorksheetButton && !accountOverlayOpened && (
            <div
              className={`lg:hidden rounded-t-2xl bg-primary-navyblue-shade p-4 pb-5 fixed left-0 w-full z-[101]`}
              style={bottomSheetOffset}
            >
              <TimedWorksheetOption
                createWorksheet={createWorksheet}
                onSetTimedWorksheet={setIsTimedWorksheetEnabled}
                isSubscriptionDelayed={isSubscriptionDelayed}
                handleShowSubscriptionDelayedError={
                  handleShowSubscriptionDelayedError
                }
              />
            </div>
          )}
          {haveMasteryLevel && <BreakdownSection />}
        </div>
      ) : null}
      {error ? (
        <Snackbar
          message={error}
          views={viewWidth < breakpoint.md ? 'mobile' : 'desktop'}
          buttonText={errorFromLTA ? 'Contact us' : 'Dismiss'}
          onTextClick={() => {
            setError('');
            if (errorFromLTA) {
              setErrorFromLTA(false);
              window.open(
                'https://geniebook.com/contact',
                '_blank',
                'noreferrer',
              );
            }
          }}
          states={'error'}
          className={'fixed center-horizontal top-[60px]'}
        />
      ) : null}
      {queryStudentTopics.isLoading ? (
        <>
          {isJC ? (
            <span
              id="jc_scrim_wrapper"
              className="[&>div]:flex [&>div]:items-center [&>div]:justify-center"
            >
              {loadingScreen}
            </span>
          ) : (
            loadingScreen
          )}
        </>
      ) : null}
      {loadingCreateWs ? (
        <LoadingFull disableDecorations={Boolean(isJC)}>
          <div
            className={`text-center text-neutral-chalk ${
              isJC ? 'flex flex-col items-center' : ''
            }`}
          >
            {Boolean(isJC) && (
              <img
                src={`${CDN_URL}assets/animations/GB-loading-minimalistic.gif`}
                className="mb-5"
                alt="loading spinner"
                width={25}
                height={25}
              />
            )}
            <p className={'mb-5 font-black text-xl leading-6'}>
              Creating Worksheet…
            </p>
            <p className={'mb-0'}>
              Take your time to learn and understand the concepts. You can do
              it!
            </p>
          </div>
        </LoadingFull>
      ) : null}

      <PopUpFailedGenerateWorksheet
        show={insufficientQuestionsPopup.show}
        subtopics={insufficientQuestionsPopup.subtopics}
        className="!max-w-[calc(100%-32px)]"
        onConfirm={() => {
          setInsufficientQuestionsPopup({
            subtopics: [],
            show: false,
          });
        }}
      />
      <BreakdownOnboarding />
    </div>
  );
};

export default ProgressDashboardV2;
